import { useCallback, useEffect, useState } from "react";

import {
  approveContract,
  approveOutreachDraft,
  contractPdfUrl,
  getContract,
  getOutreachDraft,
  rejectContract,
  rejectOutreachDraft,
  updateContract,
  updateOutreachDraft,
} from "../api";

// Right-side slide-in review surface for a single document.
// Used from DealWorkspace's "Granska"-buttons for both outreach
// drafts and contract drafts. Not a modal — the user can still see
// the underlying list dim in the background and cancel out with Esc
// or the close button.
//
// For text drafts (outreach + non-PDF contracts) we render the body
// in a Redigera-toggle textarea; the seller can inline-edit and save
// before approving. For contract PDFs we embed the FileResponse-
// streamed URL directly. After approval we render the last 8 chars
// of the approved content hash so reviewers have a stable receipt.
export default function DocReviewPanel({
  workspaceId,
  docKind,           // "outreach" | "contract"
  docId,
  onClose,
  onChanged,
}) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Local edit state — only used when the user hits "Redigera".
  const [editing, setEditing] = useState(false);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");

  // Reject workflow — shows a small textarea + Skicka button.
  const [rejectingReason, setRejectingReason] = useState(null); // string | null
  const [submitting, setSubmitting] = useState(false);

  // ── Load ───────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data =
        docKind === "outreach"
          ? await getOutreachDraft(docId)
          : await getContract(workspaceId, docId);
      setDoc(data);
      setEditSubject(data.subject || data.title || "");
      setEditBody(data.body || data.content || "");
    } catch (e) {
      setError(e?.body?.detail || e.message || "Kunde inte ladda dokumentet.");
    } finally {
      setLoading(false);
    }
  }, [docKind, docId, workspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  // Close on Escape for keyboard users.
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  // ── Actions ────────────────────────────────────────────────────────
  const saveEdit = async () => {
    setSubmitting(true);
    try {
      if (docKind === "outreach") {
        const updated = await updateOutreachDraft(docId, {
          subject: editSubject,
          body: editBody,
        });
        setDoc(updated);
      } else {
        const updated = await updateContract(workspaceId, docId, {
          title: editSubject,
          content: editBody,
        });
        setDoc(updated);
      }
      setEditing(false);
      onChanged?.();
    } catch (e) {
      setError(e?.body?.detail || e.message || "Kunde inte spara.");
    } finally {
      setSubmitting(false);
    }
  };

  const approve = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const updated =
        docKind === "outreach"
          ? await approveOutreachDraft(docId)
          : await approveContract(workspaceId, docId);
      setDoc(updated);
      onChanged?.();
    } catch (e) {
      setError(e?.body?.detail || e.message || "Kunde inte godkänna.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitReject = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const opts = { reviewNotes: rejectingReason || "" };
      const updated =
        docKind === "outreach"
          ? await rejectOutreachDraft(docId, opts)
          : await rejectContract(workspaceId, docId, opts);
      setDoc(updated);
      setRejectingReason(null);
      onChanged?.();
    } catch (e) {
      setError(e?.body?.detail || e.message || "Kunde inte avvisa.");
    } finally {
      setSubmitting(false);
    }
  };

  const hashTail = doc?.approved_content_hash
    ? doc.approved_content_hash.slice(-8)
    : null;
  const status = doc?.status || "";
  const isApproved = status === "approved";
  const isRejected = status === "rejected";
  const isPdfContract =
    docKind === "contract" && (doc?.has_pdf || doc?.pdf_available);

  return (
    <div className="qai-drp-overlay" role="dialog" aria-modal="true">
      <div className="qai-drp-backdrop" onClick={onClose} />
      <aside className="qai-drp-panel">
        <header className="qai-drp-head">
          <div>
            <div className="qai-drp-kind">
              {docKind === "outreach" ? "Outreach-utkast" : "Kontrakt"}
            </div>
            <h2 className="qai-drp-title">
              {editing
                ? "Redigerar…"
                : doc?.subject || doc?.title || "Laddar…"}
            </h2>
            {status && (
              <span
                className="qai-outreach-status-chip"
                data-status={status}
              >
                {status}
              </span>
            )}
          </div>
          <button
            type="button"
            className="qai-drp-close"
            onClick={onClose}
            aria-label="Stäng"
          >
            ×
          </button>
        </header>

        {loading && (
          <div className="qai-thread-loading">Laddar dokument…</div>
        )}

        {error && <div className="qai-error qai-drp-err">{error}</div>}

        {!loading && doc && (
          <div className="qai-drp-body">
            {isPdfContract ? (
              <iframe
                className="qai-drp-pdf"
                src={contractPdfUrl(workspaceId, docId)}
                title="Kontrakt PDF"
              />
            ) : editing ? (
              <div className="qai-drp-edit">
                <label className="qai-onboard-field">
                  <span>Ämne</span>
                  <input
                    type="text"
                    value={editSubject}
                    onChange={(e) => setEditSubject(e.target.value)}
                    disabled={submitting}
                  />
                </label>
                <label className="qai-onboard-field">
                  <span>Innehåll</span>
                  <textarea
                    className="qai-drp-textarea"
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                    rows={16}
                    disabled={submitting}
                  />
                </label>
              </div>
            ) : (
              <pre className="qai-drp-text">
                {doc.body || doc.content || "(tomt innehåll)"}
              </pre>
            )}

            {hashTail && (
              <div className="qai-drp-hash">
                Godkänd innehålls-hash: …{hashTail}
              </div>
            )}
          </div>
        )}

        <footer className="qai-drp-actions">
          {rejectingReason !== null ? (
            <div className="qai-drp-reject">
              <textarea
                placeholder="Kort motivering (valfritt)"
                rows={3}
                value={rejectingReason}
                onChange={(e) => setRejectingReason(e.target.value)}
                disabled={submitting}
              />
              <div className="qai-drp-reject-actions">
                <button
                  type="button"
                  className="qai-onboard-secondary"
                  onClick={() => setRejectingReason(null)}
                  disabled={submitting}
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  className="qai-drp-reject-btn"
                  onClick={submitReject}
                  disabled={submitting}
                >
                  {submitting ? "…" : "Avvisa"}
                </button>
              </div>
            </div>
          ) : editing ? (
            <>
              <button
                type="button"
                className="qai-onboard-secondary"
                onClick={() => setEditing(false)}
                disabled={submitting}
              >
                Avbryt
              </button>
              <button
                type="button"
                className="qai-onboard-primary"
                onClick={saveEdit}
                disabled={submitting}
              >
                {submitting ? "Sparar…" : "Spara"}
              </button>
            </>
          ) : (
            <>
              {!isPdfContract && !isApproved && !isRejected && (
                <button
                  type="button"
                  className="qai-onboard-secondary"
                  onClick={() => setEditing(true)}
                >
                  Redigera
                </button>
              )}
              {!isRejected && (
                <button
                  type="button"
                  className="qai-drp-reject-btn"
                  onClick={() => setRejectingReason("")}
                  disabled={submitting}
                >
                  Avvisa
                </button>
              )}
              {!isApproved && (
                <button
                  type="button"
                  className="qai-onboard-primary"
                  onClick={approve}
                  disabled={submitting}
                >
                  {submitting ? "…" : "Godkänn"}
                </button>
              )}
            </>
          )}
        </footer>
      </aside>
    </div>
  );
}
